import { useEffect } from 'react';
import styled from 'styled-components';
import { ReactComponent as CopyIcon } from '@elementary/common-ui/assets/copy-2.svg';
import {
  FlexColumn,
  TextField,
  FormField,
  FlexSpace,
  IconButton,
  ToggleButton,
  Link,
} from '@elementary/common-ui/components';
import { Fade, ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import useSnackbar from 'shared/hooks/useSnackbar';
import { EnvScheduleForm } from 'shared/types';

const tokenConfigOptions = [
  { value: 'configure', label: 'Configure your own secret token' },
  { value: 'generate', label: 'Generate a new secret token' },
];

const getRandomToken = () => {
  const randomBytesArray = new Uint8Array(32);
  crypto.getRandomValues(randomBytesArray);

  const randomHex = Array.from(randomBytesArray)
    .map((byte) => byte.toString(16))
    .join('');

  return randomHex;
};

const ClipboardCopyBox = styled(FlexSpace)`
  background: ${({ theme }) => theme.palette.common.black};
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray7};
  border-radius: 8px;
  .MuiTypography-root {
    color: ${({ theme }) => theme.palette.common.orange2};
    font-family: 'Geist Mono', monospace;
  }
`;

const CopyButtonStyled = styled(IconButton)`
  padding: 0;
  svg {
    width: 16px;
    path {
      fill: ${({ theme }) => theme.palette.common.white};
    }
  }
`;

const ToggleButtonStyled = styled(ToggleButton)`
  color: ${({ theme }) => theme.palette.common.black};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  &.Mui-selected {
    background: ${({ theme }) => theme.palette.common.lightBlue3};
    color: ${({ theme }) => theme.palette.secondary.main};
  }
  &:hover {
    background: ${({ theme }) => theme.palette.common.lightBlue3} !important;
  }
`;

type Props = {
  environmentId?: string;
  methods: UseFormReturn<EnvScheduleForm, any, undefined>;
  error?: string;
};

const WebhookFormSection = ({ environmentId, error, methods }: Props) => {
  const { openSnackbar } = useSnackbar();

  const webhookUrl = `${
    import.meta.env.VITE_API_BASE_URL
  }/webhook/sync/${environmentId}`;

  const handleCopyClick = (text: string) => {
    navigator.clipboard.writeText(text);
    openSnackbar({
      duration: 3000,
      message: `Copied to clipboard`,
    });
  };

  useEffect(() => {
    const tokenConfigType = methods.watch('tokenConfigType');
    if (tokenConfigType === 'generate') {
      methods.setValue('token', getRandomToken());
    } else {
      methods.resetField('token');
    }
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch('tokenConfigType')]);

  return (
    <Fade in={methods.watch('updateMethod') === 'webhook'}>
      <FlexColumn gap={3}>
        <FormField title="Generated webhook URL">
          <ClipboardCopyBox>
            <Typography noWrap sx={{ pr: 2 }}>
              {webhookUrl}
            </Typography>
            <CopyButtonStyled onClick={() => handleCopyClick(webhookUrl)}>
              <CopyIcon />
            </CopyButtonStyled>
          </ClipboardCopyBox>
        </FormField>

        <FormField
          title={
            <Typography>
              Secret token{' '}
              <Link
                href="https://docs.elementary-data.com/cloud/guides/sync-scheduling"
                target="_blank"
              >
                Learn more
              </Link>
            </Typography>
          }
        >
          <Controller
            control={methods.control}
            name="tokenConfigType"
            render={({ field }) => (
              <ToggleButtonGroup
                {...field}
                fullWidth
                exclusive
                onChange={(e: any, val) => {
                  if (!val) {
                    methods.resetField('tokenConfigType');
                  } else {
                    field.onChange(e);
                  }
                }}
              >
                {tokenConfigOptions.map((o) => (
                  <ToggleButtonStyled
                    key={o.value}
                    value={o.value}
                    size="large"
                  >
                    {o.label}
                  </ToggleButtonStyled>
                ))}
              </ToggleButtonGroup>
            )}
          />
        </FormField>

        <FlexColumn>
          <Fade
            in={methods.watch('tokenConfigType') === 'configure'}
            exit={false}
            unmountOnExit
          >
            <div>
              <FormField title="Insert secret token" errorMessage={error}>
                <TextField
                  {...methods.register('token', {
                    required:
                      methods.watch('updateMethod') === 'webhook' &&
                      methods.watch('tokenConfigType') === 'configure',
                  })}
                  placeholder="xxxxxxxxx_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
                  autoComplete="off"
                  size="large"
                />
              </FormField>
            </div>
          </Fade>

          <Fade
            in={methods.watch('tokenConfigType') === 'generate'}
            exit={false}
            unmountOnExit
          >
            <div>
              <ClipboardCopyBox>
                <Typography noWrap sx={{ pr: 2 }}>
                  {methods.watch('token')}
                </Typography>
                <CopyButtonStyled
                  onClick={() => handleCopyClick(methods.watch('token'))}
                >
                  <CopyIcon />
                </CopyButtonStyled>
              </ClipboardCopyBox>
            </div>
          </Fade>
        </FlexColumn>
      </FlexColumn>
    </Fade>
  );
};

export default WebhookFormSection;
